import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import debounce from 'lodash/debounce';
import { getTaggedItems } from './helpers';

/**
 * FilterWrapper
 * @description Higher Order Component for Filtering
 * @version 1.0.1
 */
const FilterWrapper = (WrappedComponent) => {
  class HOC extends Component {
    constructor(props) {
      super(props);

      this.handleValuesChange = this.handleValuesChange.bind(this);
      this.handleValuesChange = debounce(this.handleValuesChange, 1200);
      this.handleFilterReset = this.handleFilterReset.bind(this);
      this.updateCustomFilters = this.updateCustomFilters.bind(this);
      this.state = {
        customFilters: this.initCustomFilterValues(),
        filters: this.initFilterValues(),
        startDate: props.startDate,
        endDate: props.endDate
      };
    }

    shouldComponentUpdate(nextProps) {
      const { parent, endDate, startDate } = this.props;
      const dateChanged = endDate !== nextProps.endDate || startDate !== nextProps.startDate;
      // update dates and call setTagsFilterState to update outgoing filters
      if (dateChanged) {
        this.setState({
          startDate: nextProps.startDate,
          endDate: nextProps.endDate
        }, () => {
          const { filters } = this.state;
          this.setTagsFilterState(filters);
        });
      }
      // check to see if wrapper has changed
      if (parent && parent !== nextProps.parent) {
        this.handleFilterReset();
      }

      return true;
    }

    /**
     * @description Merge form values with current state
     * @return {object} Form Values
     */
    getFormValues = (state, values) => ({
      ...state.filters,
      ...values
    });

    /**
     * @description Build tag array and update Form values and Custom Filters
     * @fires FilterWrapper#getTaggedItems
     * @param {array} tags Array of tag keys
     * @param {bool} allTags 'And' all tags in search (True) or 'Or' (False)
     */
    async setTagsFilterState(values) {
      const {
        tags = [], allTags = true, search, active
      } = values;
      const { endDate, startDate } = this.state;

      // Fetch the userIds associated to this tag
      await getTaggedItems(tags, allTags, startDate, endDate)
        .then(
          (response) => {
            const tagged = response.length ? response.map(item => item.object_id) : [-1];
            this.setState(prevState => ({
              filters: {
                ...prevState.filters,
                tags,
                tagged: [...new Set(tagged)], // if there are tags set to no records else no tags
                allTags
              }
            }));
            // Update the filter state
            this.setState(prevState => ({
              customFilters: {
                ...prevState.customFilters,
                item: {
                  ...prevState.customFilters.item,
                  searchValue: search
                },
                ids: {
                  ...prevState.customFilters.ids,
                  searchValue: tags.length ? [...new Set(tagged)] : []
                },
                ...(active) && {
                  active: {
                    contains: false,
                    searchFields: ['active', 'is_active'],
                    ...prevState.customFilters.active,
                    searchValue: active
                  }
                }
              }
            }), () => {
              // reload user list
              this.updateCustomFilters(values);
            });
          },
          () => {
            // Update the filter state
            this.setState(prevState => ({
              customFilters: {
                ...prevState.customFilters,
                tags,
                tagged: []
              }
            }),
            () => {
              this.updateCustomFilters(values);
            });
          }
        );
    }

    /**
     * @description Set the Form/Filters state
     * @fires Props#onUpdateCustomFilters
     */
    setFieldFilterState(values) {
      // Update the filter state for this input
      this.setState(prevState => ({
        filters: this.getFormValues(prevState, values)
      }));
    }

    /**
     * @description Set the Custom Filter state
     * @fires Props#onUpdateCustomFilters
     */
    setCustomFilterState(values) {
      const {
        tags = [], tagged, search, active
      } = values;
      this.setState(prevState => ({
        customFilters: {
          ...prevState.customFilters,
          item: {
            ...prevState.customFilters.item,
            searchValue: search
          },
          ids: {
            ...prevState.customFilters.ids,
            searchValue: tags.length ? [...new Set(tagged)] : []
          },
          ...(values.active) && {
            active: {
              contains: false,
              searchFields: ['active', 'is_active'],
              ...prevState.customFilters.active,
              searchValue: active
            }
          }
        }
      }), () => {
        // reload list
        this.updateCustomFilters(values);
      });
    }

    /**
     * @description Return the the initial Filter value shape
     * @return {object} Initial value shape
     */
    initFilterValues = () => {
      const { dataSource } = this.props;
      return {
        search: '',
        tags: [],
        allTags: true,
        active: undefined,
        tagged: [],
        ...dataSource
      };
    };

    /**
     * @description Return the initial custom filter object
     * @return {object} Custom filter
     */
    initCustomFilterValues = () => {
      const { initCustomFilterValues } = this.props;
      return {
        item: {
          contains: false,
          searchFields: ['q'],
          searchValue: ''
        },
        ids: {
          contains: false,
          searchFields: ['id__in'],
          searchValue: []
        },
        ...initCustomFilterValues
      };
    };

    /**
     * @description Update state customFilters based on active
     * @param {values} Object
     * @fires onUpdateCustomFilters
     */
    updateCustomFilters(values) {
      const { active } = values;
      const { onUpdateCustomFilters } = this.props;
      // reload user list
      const { customFilters } = this.state;
      if (!active) {
        delete customFilters['active']; // eslint-disable-line dot-notation
      }
      onUpdateCustomFilters(customFilters);
    }

    /**
     * @description Update filters based on Form Values
     * @fires FormWrapper#setFieldFilterState
     * @fires FormWrapper#setCustomFilterState
     * @fires FormWrapper#setTagsFilterState
     */
    handleValuesChange(values) {
      const { onUpdate } = this.props;
      const { filters } = this.state;
      // provide changes to wrapper
      onUpdate(values);
      // keep the form values updated and clean
      const formValues = {
        ...filters,
        ...values
      };

      // if the input is searched
      if (Object.keys(values)
        .includes('search') || Object.keys(values)
        .includes('active')) {
        this.setFieldFilterState(formValues); // update the form fields in state for dataSource
        this.setCustomFilterState(formValues); // update the customFilters state for the list
      }
      else {
        // else the user is filtering by tags
        // Fetch the userIds associated to this tag
        // and update the filter state
        this.setTagsFilterState(formValues);
      }
    }

    /**
     * @description Reset the Custom Filters and Form values to default
     * @fires Props#onUpdateCustomFilters
     */
    handleFilterReset() {
      const { onUpdateCustomFilters } = this.props;
      const customFilterValues = this.initCustomFilterValues();

      this.setState({
        filters: {
          ...this.initFilterValues()
        }
      });
      this.setState(prevState => ({
        customFilters: {
          ...prevState.customFilters,
          ...customFilterValues
        }
      }), () => {
        // reload list
        const { customFilters } = this.state;
        onUpdateCustomFilters(customFilters);
      });
    }

    render() {
      const { filters } = this.state;
      const { dataSource } = this.props;

      const data = _.isEmpty(filters) ? dataSource : filters;

      return (
        <WrappedComponent
          onFieldsChange={this.handleFieldsChange}
          onResetClick={this.handleFilterReset}
          onValuesChange={this.handleValuesChange}
          {...this.props}
          dataSource={data}
        />
      );
    }
  }

  HOC.propTypes = {
    initCustomFilterValues: PropTypes.object,
    dataSource: PropTypes.object,
    endDate: PropTypes.object,
    startDate: PropTypes.object,
    reducerKey: PropTypes.string,
    placeholder: PropTypes.string,
    themeName: PropTypes.string,
    onUpdateCustomFilters: PropTypes.func,
    onUpdate: PropTypes.func,
    parent: PropTypes.number
  };

  HOC.defaultProps = {
    initCustomFilterValues: {},
    dataSource: {},
    endDate: null,
    startDate: null,
    reducerKey: '',
    placeholder: '',
    themeName: '',
    onUpdateCustomFilters() {
    },
    onUpdate() {
    },
    parent: null
  };

  return HOC;
};

export default FilterWrapper;
